<script setup>
import ExportSegmentationCustomerAction from '@frontJs/components/actions/projectManagement/sem/ExportSegmentationCustomerAction.vue';
import EyeIcon from '@iconify-vue/ph/eye';
import PencilSimpleIcon from '@iconify-vue/ph/pencil-simple';
import PlusIcon from '@iconify-vue/ph/plus';
import TrayArrowDownIcon from '@iconify-vue/ph/tray-arrow-down';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import BaseCell from '@js/components/tables/cells/BaseCell.vue';
import HeaderCell from '@js/components/tables/cells/HeaderCell.vue';
import NumberCell from '@js/components/tables/cells/NumberCell.vue';
import PillCell from '@js/components/tables/cells/PillCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { useRoute } from '@js/utils/route';
import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';

const { tenant } = defineProps({
    tenant: {
        type: Object,
        required: true,
    },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
    loading,
    table,
    count,
    globalFilter,
    filters,
    availableFilters,
    setGlobalFilter,
    setFilters,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'name',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Nom',
                }),
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.name,
                    length: 160,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'type',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Type',
                }),
            cell: (props) =>
                h(PillCell, {
                    pillValue: props.row.original.type.label,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'total',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Total',
                }),
            cell: (props) =>
                props.row.original.total
                    ? h(NumberCell, {
                          number: props.row.original.total,
                      })
                    : null,
            enableSorting: false,
        }),
        columnHelper.display({
            id: 'actions',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Actions',
                    width: 0,
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () => [
                            h(ActionButton, {
                                size: 'small',
                                title: 'Modifier',
                                href: route(
                                    'front.projectManagement.sem.segmentation',
                                    {
                                        tenant: tenant.id,
                                        semSegmentation: props.row.original.id,
                                    },
                                ),
                                icon: PencilSimpleIcon,
                                square: true,
                            }),
                            h(ActionButton, {
                                size: 'small',
                                title: 'Voir les clients',
                                href: route(
                                    'front.projectManagement.sem.segmentationCustomers',
                                    {
                                        tenant: tenant.id,
                                        semSegmentation: props.row.original.id,
                                    },
                                ),
                                type: 'secondary',
                                icon: EyeIcon,
                                square: true,
                            }),
                            h(
                                ExportSegmentationCustomerAction,
                                {
                                    tenant: tenant,
                                    semSegmentation: props.row.original,
                                },
                                {
                                    button: ({ action, loading }) =>
                                        h(ActionButton, {
                                            size: 'small',
                                            title: 'Exporter les clients',
                                            type: 'secondary',
                                            callback: () => action(),
                                            loading,
                                            square: true,
                                            icon: TrayArrowDownIcon,
                                        }),
                                },
                            ),
                        ],
                    },
                ),
        }),
    ],
    url: route('front.projectManagement.sem.postSegmentations', {
        tenant: tenant.id,
    }),
    columnPinning: {
        right: ['actions'],
    },
});
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :filters="filters"
        :available-filters="availableFilters"
        :set-filters="setFilters"
        :set-global-filter="setGlobalFilter"
        :loading="loading"
    >
        <template #actions>
            <ActionButton
                size="small"
                :href="
                    route('front.projectManagement.sem.segmentation', {
                        tenant: tenant.id,
                    })
                "
                :left-icon="PlusIcon"
            >
                Ajouter
            </ActionButton>
        </template>
    </DataTableLayout>
</template>
